import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, API_URL, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import Countdown from "@/components/Countdown";
import CheckoutDialog from "@/components/CheckoutDialog";
import PhotoUploadDialog from "@/components/PhotoUploadDialog";
import AdminPressCard from "@/components/AdminPressCard";
import AdminPhotosCard from "@/components/AdminPhotosCard";
import AdminResetCard from "@/components/AdminResetCard";

const EASE = [0.16, 1, 0.3, 1];

export default function Dashboard() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [pixels, setPixels] = useState(null);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [uploadPixel, setUploadPixel] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/pixels/mine");
      setPixels(data.pixels);
    } catch {
      setPixels([]);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <main className="min-h-screen bg-[#FAF8F5] pt-[57px]" data-testid="dashboard-page">
      <div className="px-5 md:px-12 py-14 border-b border-border/60">
        <p className="text-[10px] tracking-[0.4em] text-golddark mb-4">
          {t("dash.welcome").toUpperCase()} {user?.name?.toUpperCase()}
        </p>
        <h1 className="font-display font-black tracking-tighter text-4xl md:text-6xl" data-testid="dashboard-title">
          {t("dash.title")}
        </h1>
        <p className="mt-3 text-xs text-muted-foreground tracking-[0.15em]">{t("dash.subtitle")}</p>
      </div>

      <div className="px-5 md:px-12 py-12 grid gap-4 md:grid-cols-3">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE }}
          className="border border-border/60 p-8"
        >
          <div className="text-[10px] tracking-[0.3em] text-muted-foreground">{t("stats.sold").toUpperCase()} — {t("dash.title").toUpperCase()}</div>
          <div className="mt-3 font-mono text-6xl font-light text-golddark tabular-nums" data-testid="dashboard-pixel-count">
            {pixels?.length ?? 0}
          </div>
          <button
            onClick={() => setCheckoutOpen(true)}
            data-testid="dashboard-buy-more-btn"
            className="mt-8 px-6 py-3 bg-[#7D8C77] text-[#FAF8F5] text-[10px] tracking-[0.25em] font-semibold hover:bg-[#3C3735] transition-colors duration-200"
          >
            {(pixels && pixels.length > 0 ? t("grid.buyAny") : t("dash.buyFirst")).toUpperCase()}
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
          className="border border-border/60 p-8 flex items-center justify-center"
        >
          <Countdown />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2, ease: EASE }}
          className="border border-gold/40 p-8"
        >
          <div className="text-[10px] tracking-[0.3em] text-golddark">HUMAN MOSAIC</div>
          <p className="mt-4 font-display italic text-xl leading-snug">"In a gentle way, you can shake the world."</p>
          <p className="mt-3 text-[10px] tracking-[0.3em] text-muted-foreground">— M. K. GANDHI</p>
        </motion.div>
      </div>

      <div className="px-5 md:px-12 pb-20">
        {pixels === null ? null : pixels.length === 0 ? (
          <div className="border border-dashed border-border p-16 text-center" data-testid="dashboard-empty-state">
            <p className="text-sm text-muted-foreground">{t("dash.empty")}</p>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="dashboard-pixels-grid">
            {pixels.map((p, i) => (
              <motion.div
                key={p.index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: i * 0.06, ease: EASE }}
                className="border border-border/60 p-6 flex gap-5 items-center hover:border-gold/60 transition-colors duration-300"
                data-testid={`pixel-card-${p.index}`}
              >
                <div className="w-20 h-20 shrink-0 border border-border bg-[#FFFFFF] overflow-hidden">
                  {p.photo_path ? (
                    <img
                      src={`${API_URL}/files/${p.photo_path}`}
                      alt={t("alts.face")}
                      className="w-full h-full object-cover"
                      data-testid={`pixel-photo-${p.index}`}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[8px] tracking-[0.2em] text-muted-foreground text-center px-1">
                      400×400
                    </div>
                  )}
                </div>
                <div className="min-w-0">
                  <div className="text-[9px] tracking-[0.3em] text-muted-foreground">{t("dash.pixelLabel")}</div>
                  <div className="font-mono text-xl text-[#3C3735] tabular-nums">
                    [{p.x}, {p.y}]
                  </div>
                  <div className={`mt-1 text-[9px] tracking-[0.25em] ${p.photo_path ? "text-golddark" : "text-muted-foreground"}`}>
                    {(p.photo_path ? t("dash.photoOk") : t("grid.noPhoto")).toUpperCase()}
                  </div>
                  <button
                    onClick={() => setUploadPixel(p.index)}
                    data-testid={`pixel-upload-btn-${p.index}`}
                    className="mt-3 px-4 py-2 border border-border text-[9px] tracking-[0.25em] hover:bg-[#7D8C77] hover:text-[#FAF8F5] hover:border-gold transition-colors duration-200"
                  >
                    {(p.photo_path ? t("dash.changePhoto") : t("dash.addPhoto")).toUpperCase()}
                  </button>
                  <a
                    href={`/certificato/${p.index}`}
                    data-testid={`pixel-cert-link-${p.index}`}
                    className="inline-block mt-3 ml-2 px-4 py-2 border border-gold/50 text-[9px] tracking-[0.25em] text-golddark hover:bg-[#7D8C77] hover:text-[#FAF8F5] transition-colors duration-200"
                  >
                    {t("cert.eyebrow").toUpperCase()}
                  </a>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {user?.role === "admin" && <AdminPhotosCard t={t} />}
      {user?.role === "admin" && <AdminResetCard t={t} />}
      {user?.role === "admin" && <AdminPressCard t={t} />}
      {user?.role === "admin" && <AdminPortraitCard t={t} />}

      <CheckoutDialog
        open={checkoutOpen}
        onOpenChange={setCheckoutOpen}
        requestedIndex={null}
        onSuccess={(indices) => {
          load();
          if (indices && indices.length > 0) setUploadPixel(indices[0]);
        }}
      />
      <PhotoUploadDialog
        open={uploadPixel != null}
        onOpenChange={(o) => !o && setUploadPixel(null)}
        pixelIndex={uploadPixel}
        onDone={() => {
          setUploadPixel(null);
          load();
        }}
      />
    </main>
  );
}

function AdminPortraitCard({ t }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await api.post("/admin/portrait", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("admin.success"));
      setFile(null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="px-5 md:px-12 pb-16" data-testid="admin-portrait-card">
      <div className="border border-gold/50 p-8">
        <div className="text-[10px] tracking-[0.3em] text-golddark">ADMIN</div>
        <h3 className="mt-2 font-display text-2xl font-bold tracking-tight">{t("admin.portraitTitle")}</h3>
        <p className="mt-2 text-xs text-muted-foreground leading-relaxed max-w-2xl">{t("admin.portraitNote")}</p>
        <div className="mt-6 flex flex-wrap items-center gap-4">
          <label
            data-testid="admin-portrait-choose-btn"
            className="px-6 py-3 border border-border text-[10px] tracking-[0.25em] cursor-pointer hover:bg-[#3C3735] hover:text-[#FAF8F5] transition-colors duration-200"
          >
            {t("upload.choose").toUpperCase()}
            <input
              type="file"
              accept="image/*"
              className="hidden"
              data-testid="admin-portrait-input"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
          </label>
          {file && <span className="text-xs text-muted-foreground font-mono">{file.name}</span>}
          <button
            onClick={upload}
            disabled={!file || busy}
            data-testid="admin-portrait-upload-btn"
            className="px-6 py-3 bg-[#7D8C77] text-[#FAF8F5] text-[10px] tracking-[0.25em] font-semibold hover:bg-[#3C3735] disabled:opacity-40 transition-colors duration-200"
          >
            {busy ? t("common.loading").toUpperCase() : t("admin.upload").toUpperCase()}
          </button>
        </div>
      </div>
    </div>
  );
}
